import { Dialog } from "primereact/dialog";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  deleteStaffShift,
  Staff
} from "../../helpers/workTimeScheduleHelper";
import React from "react";
import useMutationHook from "../../hooks/useMutationHook";
import { useQueryClient } from "react-query";
import { ToastMessageProps } from "../../constants/staticTypes";
import { JTranslation } from "../../helpers/jTranslate";
import { STAFFS_WITH_SCHEDULES_AND_SHIFT } from "../../constants/queryKeys";
import { capitalize } from "lodash";

type Props = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<any>>;
  staffList: Staff[];
  selectedShift: any;
  scheduleId: string;
  setToastMessage: Dispatch<SetStateAction<ToastMessageProps>>
  callBackFn?: () => void
};

function DeleteShiftModal({
  visible,
  setVisible,
  staffList,
  selectedShift,
  scheduleId,
  setToastMessage,
  callBackFn
}: Readonly<Props>) {
  const queryClient = useQueryClient();
  const deleteStaffShiftMutation = useMutationHook(queryClient, false);

  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const closeModal = () => {
    if (!visible) return;
    setVisible(undefined);
    setSelectedStaff(null);
  };

  const onceDeleted = (async (message: string, variant: string) => {
    setToastMessage({ message, variant, show: true });
    await queryClient.refetchQueries([STAFFS_WITH_SCHEDULES_AND_SHIFT]);
    setIsLoading(false);
    closeModal();

    if (callBackFn) callBackFn();
  });

  const handleDelete = () => {
    if(!selectedShift || !selectedShift?.id) return;

    setIsLoading(true);
    deleteStaffShift(
      deleteStaffShiftMutation,
      scheduleId,
      selectedShift?.id,
      onceDeleted,
      onceDeleted
    );
  }

  const headerContent = (
    <div className="d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="m-0">
          <JTranslation typeCase="capitalize" text="Delete shift?" />
        </h5>
        <i className="pi pi-times" role="button" onClick={closeModal} />
      </div>
    </div>
  );

  const footerContent = (
    <div className='d-flex justify-content-end align-items-center'>
      <button
        type="button"
        className="btn btn-custom-primary-outline"
        onClick={closeModal}
        disabled={isLoading}
      >
        <JTranslation typeCase="pascal" text="Cancel" />
      </button>
      <button
        type="button"
        className="ms-3 btn btn-custom-primary"
        onClick={handleDelete}
        disabled={isLoading}
      >
        <span className="btn-text">
          {!isLoading
            ? <JTranslation typeCase="pascal" text="Delete" />
            : (
              <span className="d-flex align-items-center justify-content-center">
                <JTranslation typeCase="pascal" text="Deleting" />
                <i className="pi pi-spin pi-spinner ms-2" style={{ fontSize: '1rem' }} />
              </span>
            )
          }
        </span>
      </button>
    </div>
  )

  useEffect(() => {
    if (visible && staffList.length) {
      const staff = staffList.find((staff) => staff?.id === (selectedShift as any)?.tenantStaffId);
      setSelectedStaff(staff ?? {} as Staff);
    }
  }, [visible, staffList, selectedShift]);

  return (
    <Dialog
      visible={visible}
      modal
      header={headerContent}
      footer={footerContent}
      style={{ width: "30rem" }}
      onHide={closeModal}
      draggable={false}
      closeIcon={false}
      closable={false}
      closeOnEscape={false}
      pt={{
        content: { style: { padding: "0rem 1rem" } },
        footer: { style: { padding: "1rem" } },
        header: { style: { padding: "1.25rem 1rem" } },
      }}
    >
      <div className='d-flex flex-column'>
        <p className='m-0'>
          <strong>
            {capitalize(selectedStaff?.preferredName) ?? capitalize(`${selectedStaff?.firstName} ${selectedStaff?.lastName}`)}
          </strong>
        </p>
        <div className="tooltip-row">
          <div className="tooltip-label">
            {capitalize(selectedShift?.assignedShifts?.role)}
          </div>
          <div className="tooltip-separator">:</div>
          <div className="tooltip-value">
            {selectedShift?.assignedShifts?.startTime} - {selectedShift?.assignedShifts?.endTime}
          </div>
        </div>
      </div>
    </Dialog>
  );
}

export default React.memo(DeleteShiftModal);
